import { useState } from "react";
import PropTypes from "prop-types";
import { Box } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Check, ChevronDown } from "lucide-react";

import { DropdownMenuContent, DropdownMenuItem } from "./DropdownMenu";
import { BADGE_TONES } from "../theme/badgeTones";

// Colores clásicos del Badge -> tono
const COLOR_TO_TONE = {
  green: "done",
  orange: "pending",
  red: "blocked",
  gray: "muted",
  blue: "info",
  purple: "purple",
  pink: "active",
};

const VARIANT_TO_TONE = {
  default: "active",
  secondary: "muted",
  destructive: "blocked",
};

const resolveTone = (color, variant) =>
  BADGE_TONES[color] ||
  BADGE_TONES[COLOR_TO_TONE[color]] ||
  BADGE_TONES[VARIANT_TO_TONE[variant]] ||
  null;

// Diseño del chip del CRM: un único tamaño en toda la plataforma.
// `size` se sigue aceptando por compatibilidad, pero ya no cambia nada.
const BADGE_SIZE_STYLES = { px: "0.5rem", py: "0.375rem", fontSize: "0.75rem" };

const Dot = () => (
  <Box
    component="span"
    sx={{
      width: 6,
      height: 6,
      borderRadius: "50%",
      backgroundColor: "currentColor",
      flexShrink: 0,
    }}
  />
);

// Compatibilidad con las clases tailwind antiguas (bg-*-100, text-*-800...)
const LEGACY_CLASS_TONES = [
  ["red", "blocked"],
  ["yellow", "yellow"],
  ["orange", "pending"],
  ["green", "done"],
  ["blue", "info"],
  ["purple", "purple"],
  ["gray", "muted"],
];

const parseLegacyClasses = (className) => {
  if (!className) return {};
  const styles = {};
  LEGACY_CLASS_TONES.forEach(([name, toneName]) => {
    const tone = BADGE_TONES[toneName];
    if (className.includes(`bg-${name}-100`)) styles.backgroundColor = tone.background;
    if (className.includes(`text-${name}-800`)) styles.color = tone.color;
  });
  return styles;
};

/**
 * Badge único de la plataforma (píldora).
 *
 * - `selector={false}` (por defecto): badge estático, sin flecha y sin clic propio.
 * - `selector={true}`: añade la flecha y abre un menú con `options`
 *   ([{ value, label, color }]); al elegir llama a `onChange(value)`.
 * - `dot={false}`: oculta el punto de color.
 */
export const Badge = ({
  variant = "default",
  // eslint-disable-next-line no-unused-vars
  size,
  color = null,
  selector = false,
  dot = true,
  value,
  options = [],
  onChange,
  children,
  className = "",
  sx = {},
  ...props
}) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState(null);

  const tone = resolveTone(color, variant);
  const isSelectable = Boolean(selector && onChange && options.length);

  const baseSx = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 0.5,
    minWidth: "3rem",
    borderRadius: "1rem",
    fontWeight: 500,
    lineHeight: 1,
    whiteSpace: "nowrap",
    textTransform: "none",
    border: "1px solid transparent",
    transition: "filter 0.15s, background-color 0.15s",
    ...BADGE_SIZE_STYLES,
    ...(tone
      ? { color: tone.color, backgroundColor: tone.background }
      : variant === "outline"
        ? {
          color: theme.palette.text.primary,
          backgroundColor: "transparent",
          borderColor: theme.palette.divider,
        }
        : { color: BADGE_TONES.muted.color, backgroundColor: BADGE_TONES.muted.background }),
    ...parseLegacyClasses(className),
  };

  if (!isSelectable) {
    return (
      <Box component="span" sx={{ ...baseSx, ...sx }} {...props}>
        {dot ? <Dot /> : null}
        {children}
      </Box>
    );
  }

  return (
    <>
      <Box
        component="button"
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          setAnchorEl(event.currentTarget);
        }}
        sx={{
          ...baseSx,
          cursor: "pointer",
          fontFamily: "inherit",
          "&:hover": { filter: "brightness(0.96)" },
          ...sx,
        }}
        {...props}
      >
        {dot ? <Dot /> : null}
        {children}
        <ChevronDown size={13} style={{ marginLeft: -2, opacity: 0.8 }} />
      </Box>
      <DropdownMenuContent
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        align="end"
        onClick={(event) => event.stopPropagation()}
      >
        {options.map((option) => {
          const optionTone = resolveTone(option.color) || BADGE_TONES.muted;
          return (
            <DropdownMenuItem
              key={option.value}
              onClick={(event) => {
                event.stopPropagation();
                setAnchorEl(null);
                if (option.value !== value) onChange(option.value);
              }}
            >
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  backgroundColor: optionTone.color,
                  flexShrink: 0,
                }}
              />
              <Box component="span" sx={{ flex: 1 }}>
                {option.label}
              </Box>
              {option.value === value ? <Check size={14} /> : null}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </>
  );
};

Badge.propTypes = {
  variant: PropTypes.oneOf(["default", "secondary", "destructive", "outline"]),
  size: PropTypes.oneOf(["sm", "default", "lg"]),
  color: PropTypes.string,
  selector: PropTypes.bool,
  dot: PropTypes.bool,
  value: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.node.isRequired,
      color: PropTypes.string,
    })
  ),
  onChange: PropTypes.func,
  children: PropTypes.node,
  className: PropTypes.string,
  sx: PropTypes.oneOfType([PropTypes.object, PropTypes.func, PropTypes.array]),
};
